import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { UserSearch, UserSearchRow } from '@metron/shared';
import {
  parseUserSearchParams,
  SEARCH_DEFAULT_PAGE_SIZE,
  toUserSearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  USER_ROLE_LABELS,
  USER_STATUS_LABELS,
  UserSearchFilters,
} from '../components/user-search/UserSearchFilters.tsx';
import { prefixedKey, useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useUserFilter } from '../hooks/useUserFilter.ts';
import { useCustomers } from '../hooks/useCustomers.ts';
import { USERS_TABLE_URL_PREFIX } from '../components/users-table/useUsersTableFilters.ts';

const na = <span className='text-muted-foreground'>—</span>;

function roleLabel(role: string | null) {
  if (!role) return na;
  return role in USER_ROLE_LABELS ? USER_ROLE_LABELS[role as keyof typeof USER_ROLE_LABELS] : role;
}

// Column ids match the spec's sortBy values where the server sorts on them.
const columns: ColumnDef<UserSearchRow>[] = [
  {
    accessorKey: 'email',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.email}</div>,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
    cell: ({ row }) => row.original.name || na,
  },
  {
    id: 'customers',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customers' />,
    cell: ({ row }) => row.original.customers.map((c) => c.name).join(', ') || na,
  },
  {
    id: 'rbacRole',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Role' />,
    cell: ({ row }) => roleLabel(row.original.rbacRole),
  },
  {
    id: 'status',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
    cell: ({ row }) =>
      row.original.enabled
        ? USER_STATUS_LABELS[row.original.userStatus] ?? row.original.userStatus
        : 'Disabled',
  },
  {
    accessorKey: 'lastLoginAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Last Login' />,
    cell: ({ row }) => {
      const value = row.original.lastLoginAt;
      return value
        ? new Date(value).toLocaleString()
        : <span className='text-muted-foreground'>Never</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Created' />,
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  },
];

/**
 * User Search (Smart Search results, MET-3530)
 *
 * WaterScope Plus users only. There's no per-user page, so a row opens the
 * Users page with its search box set to that user's email.
 */
export function UserSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parseUserSearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = useUserFilter(spec);

  const setSpec = (next: UserSearch) => setSearchParams(toUserSearchParams(next));
  const openUser = (user: UserSearchRow) =>
    navigate(
      `/users?${new URLSearchParams({ [prefixedKey(USERS_TABLE_URL_PREFIX, 'q')]: user.email })}`,
    );

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>User Search</h1>
        <p className='text-muted-foreground'>
          Filter WaterScope Plus users by customer, role, status, and login activity. Share the URL
          to share the search.
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/users'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <UserSearchFilters
                spec={parsed.data}
                customers={customers}
                customersLoading={customersLoading}
                onChange={setSpec}
              />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} user${data?.total === 1 ? '' : 's'}${
                    isFetching ? ' · updating…' : ''
                  }`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.users ?? []}
                    onRowClick={openUser}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading ? 'Searching…' : 'No users match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
